import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Mail, 
  Lock, 
  ArrowRight, 
  Shield,
  AlertCircle,
  Loader2,
  CheckCircle,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { proposalService, type ShareLinkType } from '@/services/ProposalService';
import { useAuthStore } from '@/stores/authStore';

interface ShareLinkAccessProps {
  linkType: ShareLinkType;
}

import { 
  getStoredSession, 
  storeSession, 
  clearSession,
  type StoredSession 
} from '@/services/PublicSessionManager';

export function ShareLinkAccess({ linkType }: ShareLinkAccessProps) {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  // Page state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkInfo, setLinkInfo] = useState<{ event_name: string; client_name: string } | null>(null);
  
  // Auth flow state
  const [step, setStep] = useState<'email' | 'otp' | 'verified'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string>('');
  
  // Loading states
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  
  // Get current user from auth store
  const user = useAuthStore((state) => state.user);
  const fetchMe = useAuthStore((state) => state.fetchMe);
  
  // Check for existing session or authenticated user on mount
  useEffect(() => {
    async function init() {
      if (!token) {
        setError('Invalid link');
        setLoading(false);
        return;
      }
      
      try {
        // Try to fetch user if not in store (they might be logged in but store not populated)
        let currentUser = user;
        if (!currentUser) {
          try {
            await fetchMe(true); // skipRedirect = true for public pages
            currentUser = useAuthStore.getState().user;
          } catch (err) {
            // User not logged in, continue to OTP flow
          }
        }
        
        // First, if user is logged in on the platform, try to access data directly
        // This bypasses OTP for platform users with org access
        if (currentUser) {
          try {
            // Try to load the data directly - if it succeeds, the user has access
            const dataEndpoint = linkType === 'proposal' 
              ? proposalService.getProposalData(token)
              : proposalService.getResearchData(token);
            
            await dataEndpoint;
            
            // Success! User has access - redirect to view
            setStep('verified');
            setLoading(false);
            navigate(`/${linkType === 'proposal' ? 'p' : 'r'}/${token}/view`, { replace: true });
            return;
          } catch (err: any) {
            // Data access failed - user might not have org access, fall through to OTP
            console.error('[ShareLinkAccess] ❌ Platform user access check failed:', {
              error: err.message,
              status: err.status,
              response: err.response,
              stack: err.stack
            });
            // Don't show error to user - silently fall through to OTP flow
            // This is expected if user doesn't have org membership for this share link
          }
        }
        
        // Check for existing valid OTP session
        const storedSession = getStoredSession(token);
        if (storedSession) {
          // Verify session is still valid on server
          const check = await proposalService.checkSession(token, storedSession.sessionId);
          if (check.is_valid) {
            setEmail(storedSession.email);
            setSessionId(storedSession.sessionId);
            setStep('verified');
            setLoading(false);
            // Navigate to content page
            navigate(`/${linkType === 'proposal' ? 'p' : 'r'}/${token}/view`, { replace: true });
            return;
          }
        }
        
        // Fetch link info
        const info = await proposalService.getPublicLinkInfo(token);
        setLinkInfo(info);
        
        // If link is public (or null/undefined, which defaults to public), skip authentication and go directly to view
        if (info.is_public !== false) {
          setStep('verified');
          setLoading(false);
          navigate(`/${linkType === 'proposal' ? 'p' : 'r'}/${token}/view`, { replace: true });
          return;
        }
        
        setLoading(false);
      } catch (err: any) {
        console.error('Failed to load share link:', err);
        if (err.message?.includes('404') || err.message?.includes('not found')) {
          setError('This link is invalid or has expired');
        } else if (err.message?.includes('inactive')) {
          setError('This link has been disabled');
        } else {
          setError('Failed to load link. Please try again.');
        }
        setLoading(false);
      }
    }
    
    init();
  }, [token, linkType, navigate, user, fetchMe]);
  
  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !email.trim()) return;
    
    setSubmitting(true);
    setError(null);
    setCodeSent(false);
    
    try {
      const result = await proposalService.requestOtp(token, email.trim().toLowerCase());
      setSessionId(result.session_id);
      setMaskedEmail(result.masked_email);
      setCodeSent(true);
      setStep('otp');
    } catch (err: any) {
      console.error('Failed to request OTP:', err);
      // Parse specific error messages
      if (err.message?.includes('domain') || err.message?.includes('authorized')) {
        setError('Your email domain is not authorized to access this content. Please contact the sender.');
      } else if (err.message?.includes('wait')) {
        // Extract wait time from error details if available
        const waitMatch = err.message.match(/(\d+)\s*seconds?/i);
        const waitTime = waitMatch ? parseInt(waitMatch[1], 10) : 30;
        setError(`Please wait ${waitTime} seconds before requesting a new code.`);
      } else {
        setError(err.message || 'Failed to send verification code. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }
  
  async function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !sessionId || !otp.trim()) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      const result = await proposalService.verifyOtp(token, sessionId, otp.trim());
      
      // Store session metadata for future visits (sessionId is in httpOnly cookie)
      // We store metadata in localStorage for UI purposes
      storeSession({
        token,
        sessionId: result.session_id, // Keep for backward compatibility during migration
        email: result.email,
        expiresAt: result.session_expires_at,
      });
      
      setStep('verified');
      
      // Navigate to content page
      navigate(`/${linkType === 'proposal' ? 'p' : 'r'}/${token}/view`, { replace: true });
    } catch (err: any) {
      console.error('Failed to verify OTP:', err);
      if (err.message?.includes('expired')) {
        setError('Verification code has expired. Please request a new one.');
        setStep('email');
        setCodeSent(false);
      } else if (err.message?.includes('attempts')) {
        setError('Too many incorrect attempts. Please request a new code.');
        setStep('email');
        setCodeSent(false);
      } else if (err.message?.includes('Invalid')) {
        setError(err.message);
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }
  
  async function handleResendOtp() {
    if (!token || !email.trim()) return;
    
    setResending(true);
    setError(null);
    setCodeSent(false);
    
    try {
      const result = await proposalService.requestOtp(token, email.trim().toLowerCase());
      setSessionId(result.session_id);
      setMaskedEmail(result.masked_email);
      setOtp('');
      setCodeSent(true);
    } catch (err: any) {
      // Parse specific error messages
      if (err.message?.includes('wait')) {
        const waitMatch = err.message.match(/(\d+)\s*seconds?/i);
        const waitTime = waitMatch ? parseInt(waitMatch[1], 10) : 30;
        setError(`Please wait ${waitTime} seconds before requesting a new code.`);
      } else {
        setError(err.message || 'Failed to resend code. Please try again.');
      }
    } finally {
      setResending(false);
    }
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Error state (link not found/expired)
  if (error && !linkInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold">Link Not Available</h1>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const isProposal = linkType === 'proposal';
  
  // Generate dynamic meta tags
  const eventName = linkInfo?.event_name || '';
  const clientName = linkInfo?.client_name || '';
  const linkTypeLabel = isProposal ? 'Proposal' : 'Research';
  const title = linkInfo 
    ? `${linkTypeLabel} for ${eventName}${clientName ? `, ${clientName}` : ''} | Moniker Partners`
    : `${linkTypeLabel} | Moniker Partners`;
  const description = linkInfo
    ? `${linkTypeLabel.toLowerCase()} for ${eventName}${clientName ? ` for ${clientName}` : ''} | Moniker Partners`
    : `View ${linkTypeLabel.toLowerCase()} | Moniker Partners`;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {/* Open Graph tags */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 flex flex-col">
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          {/* Card */}
          <Card className="shadow-lg">
            <CardContent className="pt-8 pb-8 space-y-6">
              {/* Logo & Title */}
              <div className="text-center space-y-4">
                <div className="mb-4">
                  <img 
                    alt="Moniker" 
                    className="h-8 mx-auto object-contain" 
                    src="/images/logo_moniker_black.png"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {isProposal ? 'View Proposal' : 'View Research'}
                  </h2>
                  {linkInfo && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {linkInfo.event_name}
                      {linkInfo.client_name && ` • ${linkInfo.client_name}`}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Success Message - Code Sent */}
              {codeSent && step === 'otp' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Verification code sent to your email!</span>
                </div>
              )}
              
              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                  {error.includes('wait') ? (
                    <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  )}
                  <span>{error}</span>
                </div>
              )}
              
              {/* Email Step */}
              {step === 'email' && (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter your email to access
                    </label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      leftIcon={<Mail className="w-4 h-4" />}
                      required
                      autoFocus
                      autoComplete="email"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      We'll send you a verification code
                    </p>
                  </div>
                  
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-full"
                    isLoading={submitting}
                    rightIcon={<ArrowRight className="w-4 h-4" />}
                  >
                    Continue
                  </Button>
                </form>
              )}
              
              {/* OTP Step */}
              {step === 'otp' && (
                <form onSubmit={handleOtpSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter verification code
                    </label>
                    <p className="text-sm text-muted-foreground mb-4">
                      We sent a 6-digit code to <strong>{maskedEmail || email}</strong>
                    </p>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      leftIcon={<Lock className="w-4 h-4" />}
                      className="text-center text-lg tracking-widest"
                      required
                      autoFocus
                      maxLength={6}
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-full"
                    isLoading={submitting}
                    disabled={otp.length !== 6}
                  >
                    Verify & Access
                  </Button>
                  
                  <div className="flex items-center justify-between text-sm">
                    <button 
                      type="button"
                      onClick={() => { setStep('email'); setOtp(''); setError(null); setCodeSent(false); }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      Use different email
                    </button>
                    <button 
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resending}
                      className="text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                    >
                      {resending ? 'Sending...' : 'Resend code'}
                    </button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
          
          {/* Security Note */}
          <div className="flex items-start gap-3 text-sm text-muted-foreground">
            <Shield className="w-5 h-5 flex-shrink-0" />
            <p>
              Your access is protected. Only authorized email domains can view this content.
              Your session expires after 24 hours.
            </p>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="p-6 text-center text-sm text-muted-foreground">
        <p>© {new Date().getFullYear()} Moniker. Secure event planning.</p>
      </footer>
      </div>
    </>
  );
}
